import { useState, useEffect, useMemo } from 'react';
import {
  Search,
  Download,
  Loader2,
  Award,
  Users,
  TrendingUp,
  Crown,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  History,
  MoreVertical,
  UserX,
  EyeOff,
  Check,
  RotateCcw,
} from 'lucide-react';
import { useParams } from 'react-router-dom';
import useSWR, { mutate } from 'swr';
import axiosInstance from '@/lib/axios';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { triggerTextFileDownload } from '@/lib/trigger-download';
import { useAdminMembers } from '@/hooks/use-admin-members';
import { formatDisplayPhone } from '@/lib/phone-utils';
import { toast } from 'sonner';

interface PointsReportRow {
  rank: number;
  userId: string;
  userName: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  image: string | null;
  totalPoints: number;
  membershipStatus?: string;
  joinedAt?: string;
}

interface PointsReportSummary {
  totalMembers: number;
  totalPointsAwarded: number;
  averagePoints: number;
  topEarner: {
    userName: string;
    points: number;
  } | null;
  memberType?: string;
}

interface PointsReportResponse {
  rows: PointsReportRow[];
  hiddenUsers?: string[];
  summary: PointsReportSummary;
}

interface LogItem {
  srNo: number;
  id: string;
  points: number;
  notes: string;
  triggerType: string;
  time: string;
}

interface MemberLogsResponse {
  member: {
    userId: string;
    userName: string;
    totalPoints: number;
    email: string | null;
    phone: string | null;
    image: string | null;
  };
  logs: LogItem[];
}

interface HiddenUsersResponse {
  hiddenUsers: string[];
}

type SortField = 'totalPoints' | 'userName' | 'rank';
type SortOrder = 'asc' | 'desc';
type MemberType = 'active' | 'ex';

const fetcher = (url: string) => axiosInstance.get(url).then((res) => res.data);

function formatLogTime(dateStr: string) {
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return dateStr;
  }
}

export function PointsDashboardView() {
  const { communityId } = useParams<{ communityId: string }>();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortField, setSortField] = useState<SortField>('totalPoints');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [memberType, setMemberType] = useState<MemberType>('active');
  const [selectedMember, setSelectedMember] = useState<PointsReportRow | null>(null);

  // Hidden members modal state
  const [isConfigureHiddenOpen, setIsConfigureHiddenOpen] = useState(false);
  const [hiddenSearchQuery, setHiddenSearchQuery] = useState('');
  const [checkedHiddenUserIds, setCheckedHiddenUserIds] = useState<Set<string>>(new Set());
  const [isSavingHidden, setIsSavingHidden] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const queryParams = new URLSearchParams();
  if (debouncedSearch) queryParams.set('search', debouncedSearch);
  if (memberType === 'ex') queryParams.set('memberType', 'ex');

  const reportUrl = communityId
    ? `/api/communities/${communityId}/points-report?${queryParams.toString()}`
    : null;

  const { data, isLoading, isValidating } = useSWR<PointsReportResponse>(reportUrl, fetcher, {
    keepPreviousData: true,
  });

  const hiddenUsersUrl = communityId ? `/api/communities/${communityId}/hidden-users` : null;

  const { data: hiddenUsersData } = useSWR<HiddenUsersResponse>(hiddenUsersUrl, fetcher);

  // Fetch community members for configure hidden modal
  const { members: allCommunityMembers, isLoading: isMembersLoading } = useAdminMembers(
    communityId || '',
    { limit: 500 }
  );

  // Sync checkedHiddenUserIds whenever hiddenUsersData changes from backend
  useEffect(() => {
    if (hiddenUsersData?.hiddenUsers) {
      setCheckedHiddenUserIds(new Set(hiddenUsersData.hiddenUsers));
    }
  }, [hiddenUsersData]);

  const { data: memberLogsData, isLoading: isLogsLoading } = useSWR<MemberLogsResponse>(
    communityId && selectedMember
      ? `/api/communities/${communityId}/points-report/${selectedMember.userId}`
      : null,
    fetcher
  );

  const rawRows = useMemo(() => data?.rows ?? [], [data]);
  const summary = data?.summary;

  const sortedRows = useMemo(() => {
    const list = [...rawRows];
    list.sort((a, b) => {
      let comp = 0;
      if (sortField === 'totalPoints') {
        comp = a.totalPoints - b.totalPoints;
      } else if (sortField === 'userName') {
        comp = a.userName.localeCompare(b.userName);
      } else if (sortField === 'rank') {
        comp = a.rank - b.rank;
      }
      return sortOrder === 'desc' ? -comp : comp;
    });
    return list;
  }, [rawRows, sortField, sortOrder]);

  const filteredModalMembers = useMemo(() => {
    if (!allCommunityMembers) return [];
    if (!hiddenSearchQuery.trim()) return allCommunityMembers;
    const term = hiddenSearchQuery.toLowerCase();
    return allCommunityMembers.filter((m) => {
      const name = (m.name || '').toLowerCase();
      const email = (m.email || '').toLowerCase();
      const phone = m.phone || '';
      return name.includes(term) || email.includes(term) || phone.includes(term);
    });
  }, [allCommunityMembers, hiddenSearchQuery]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder(field === 'totalPoints' ? 'desc' : 'asc');
    }
  };

  const handleCheckboxToggle = (userId: string, isChecked: boolean) => {
    setCheckedHiddenUserIds((prev) => {
      const next = new Set(prev);
      if (isChecked) {
        next.add(userId);
      } else {
        next.delete(userId);
      }
      return next;
    });
  };

  const handleSaveHiddenMembers = async () => {
    if (!communityId) return;
    setIsSavingHidden(true);
    try {
      const hiddenArray = Array.from(checkedHiddenUserIds);
      await axiosInstance.patch(`/api/communities/${communityId}/hidden-users`, {
        hiddenUsers: hiddenArray,
      });

      // Revalidate points report and hidden users data
      if (hiddenUsersUrl) await mutate(hiddenUsersUrl);
      if (reportUrl) await mutate(reportUrl);

      setIsConfigureHiddenOpen(false);
      toast.success('Hidden member configurations saved successfully!');
    } catch (error) {
      console.error('Failed to save hidden members:', error);
      toast.error('Failed to save hidden member configurations');
    } finally {
      setIsSavingHidden(false);
    }
  };

  const handleDownloadExcel = () => {
    const headers = ['Rank', 'Username / Member Name', 'Email', 'Phone', 'Total Points'];
    const csvRows = [
      headers.join(','),
      ...sortedRows.map((r) =>
        [
          r.rank,
          `"${r.userName.replace(/"/g, '""')}"`,
          `"${(r.email || '').replace(/"/g, '""')}"`,
          `"${(r.phone ? formatDisplayPhone(r.phone) : '').replace(/"/g, '""')}"`,
          r.totalPoints,
        ].join(',')
      ),
    ];

    const bom = '\uFEFF';
    const csvContent = bom + csvRows.join('\n');
    const filename = `points-dashboard-${memberType}-${new Date().toISOString().split('T')[0]}.csv`;
    triggerTextFileDownload(filename, csvContent);
  };

  const getRankBadge = (rank: number) => {
    if (rank === 1) {
      return (
        <Badge className="bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30 gap-1 font-bold">
          <Crown className="h-3.5 w-3.5 fill-amber-500" /> #1
        </Badge>
      );
    }
    if (rank === 2) {
      return (
        <Badge className="bg-slate-400/15 text-slate-600 dark:text-slate-300 border-slate-400/30 gap-1 font-bold">
          <Award className="h-3.5 w-3.5" /> #2
        </Badge>
      );
    }
    if (rank === 3) {
      return (
        <Badge className="bg-amber-700/15 text-amber-700 dark:text-amber-600 border-amber-700/30 gap-1 font-bold">
          <Award className="h-3.5 w-3.5" /> #3
        </Badge>
      );
    }
    return <span className="font-medium text-muted-foreground">#{rank}</span>;
  };

  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-3.5 w-3.5 ml-1 opacity-50" />;
    }
    return sortOrder === 'desc' ? (
      <ArrowDown className="h-3.5 w-3.5 ml-1 text-primary" />
    ) : (
      <ArrowUp className="h-3.5 w-3.5 ml-1 text-primary" />
    );
  };

  if (isLoading && !data) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px] space-y-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-muted-foreground font-medium animate-pulse">
          Loading points dashboard...
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {memberType === 'ex' && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-amber-900 dark:text-amber-200">
          <div className="flex items-start sm:items-center gap-3 min-w-0">
            <UserX className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5 sm:mt-0" />
            <div className="min-w-0">
              <div className="font-bold text-sm">Ex-Members Points View</div>
              <div className="text-xs opacity-90 leading-normal">
                Displaying points leaderboard for former members of this community.
              </div>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setMemberType('active')}
            className="rounded-xl border-amber-500/30 hover:bg-amber-500/20 text-xs font-semibold gap-1.5 shrink-0 self-stretch sm:self-auto justify-center"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            <span>Switch to Active Members</span>
          </Button>
        </div>
      )}

      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="rounded-2xl border bg-card/50 backdrop-blur-sm shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Points
              </CardTitle>
              <div className="p-2 bg-primary/10 rounded-xl">
                <Award className="h-4 w-4 text-primary" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold tracking-tight text-foreground">
                {summary.totalPointsAwarded.toLocaleString()}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {memberType === 'ex' ? 'Points of former members' : 'Earned across active members'}
              </p>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border bg-card/50 backdrop-blur-sm shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {memberType === 'ex' ? 'Ex-Members Count' : 'Members Tracked'}
              </CardTitle>
              <div className="p-2 bg-blue-500/10 rounded-xl">
                {memberType === 'ex' ? (
                  <UserX className="h-4 w-4 text-blue-500" />
                ) : (
                  <Users className="h-4 w-4 text-blue-500" />
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold tracking-tight text-foreground">
                {summary.totalMembers}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {memberType === 'ex' ? 'Former community members' : 'Active community members'}
              </p>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border bg-card/50 backdrop-blur-sm shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Average Points
              </CardTitle>
              <div className="p-2 bg-emerald-500/10 rounded-xl">
                <TrendingUp className="h-4 w-4 text-emerald-500" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold tracking-tight text-foreground">
                {summary.averagePoints.toLocaleString()}
              </div>
              <p className="text-xs text-muted-foreground mt-1">Points per member average</p>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border bg-card/50 backdrop-blur-sm shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Top Performer
              </CardTitle>
              <div className="p-2 bg-amber-500/10 rounded-xl">
                <Crown className="h-4 w-4 text-amber-500" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-lg font-bold tracking-tight text-foreground truncate">
                {summary.topEarner ? summary.topEarner.userName : 'N/A'}
              </div>
              <p className="text-xs text-amber-600 dark:text-amber-400 font-medium mt-1">
                {summary.topEarner
                  ? `${summary.topEarner.points.toLocaleString()} points`
                  : 'No points recorded'}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        <div className="relative flex-1 max-w-sm w-full">
          {isValidating && data ? (
            <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground animate-spin pointer-events-none" />
          ) : (
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          )}
          <Input
            type="search"
            placeholder="Search username, phone, or email..."
            className="pl-9 bg-card rounded-xl w-full"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="flex items-center gap-2">
          <Button
            onClick={handleDownloadExcel}
            variant="outline"
            className="rounded-xl border-input hover:bg-accent gap-2 flex-1 sm:flex-none justify-center"
          >
            <Download className="h-4 w-4" />
            <span>Export to Excel</span>
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" className="rounded-xl h-10 w-10 shrink-0">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 rounded-xl p-1">
              <DropdownMenuItem
                onClick={() => setMemberType((prev) => (prev === 'active' ? 'ex' : 'active'))}
                className="cursor-pointer gap-2 py-2 font-medium"
              >
                {memberType === 'active' ? (
                  <>
                    <UserX className="h-4 w-4 text-amber-500" />
                    <span>View points of ex-members</span>
                  </>
                ) : (
                  <>
                    <Users className="h-4 w-4 text-primary" />
                    <span>View points of active members</span>
                  </>
                )}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setIsConfigureHiddenOpen(true)}
                className="cursor-pointer gap-2 py-2 font-medium"
              >
                <EyeOff className="h-4 w-4 text-muted-foreground" />
                <span>Configure hidden members</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="border rounded-2xl bg-card overflow-hidden shadow-sm">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/40 hover:bg-muted/40">
              <TableHead
                className="w-24 cursor-pointer select-none font-semibold"
                onClick={() => handleSort('rank')}
              >
                <div className="flex items-center">Rank {renderSortIcon('rank')}</div>
              </TableHead>

              <TableHead
                className="cursor-pointer select-none font-semibold"
                onClick={() => handleSort('userName')}
              >
                <div className="flex items-center">
                  Username / Member {renderSortIcon('userName')}
                </div>
              </TableHead>

              <TableHead className="hidden md:table-cell font-semibold">Contact Info</TableHead>

              <TableHead
                className="text-right cursor-pointer select-none font-semibold pr-6"
                onClick={() => handleSort('totalPoints')}
              >
                <div className="flex items-center justify-end">
                  Total Points {renderSortIcon('totalPoints')}
                </div>
              </TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {sortedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="h-32 text-center text-muted-foreground">
                  {memberType === 'ex'
                    ? 'No ex-members found matching your search.'
                    : 'No members found matching your search.'}
                </TableCell>
              </TableRow>
            ) : (
              sortedRows.map((row) => {
                const initials = row.userName
                  .split(' ')
                  .map((n) => n[0])
                  .join('')
                  .substring(0, 2)
                  .toUpperCase();

                return (
                  <TableRow key={row.userId} className="hover:bg-muted/30 transition-colors">
                    <TableCell className="font-medium">{getRankBadge(row.rank)}</TableCell>

                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-9 w-9 border border-border shrink-0">
                          <AvatarImage src={row.image || undefined} alt={row.userName} />
                          <AvatarFallback className="bg-primary/10 text-primary text-xs font-bold">
                            {initials}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <button
                            type="button"
                            onClick={() => setSelectedMember(row)}
                            className="font-semibold text-foreground hover:text-primary hover:underline text-left transition-colors flex items-center gap-1.5 group truncate"
                          >
                            <span className="truncate">{row.userName}</span>
                            <History className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 text-primary transition-opacity shrink-0" />
                          </button>
                          <div className="text-xs text-muted-foreground md:hidden truncate">
                            {row.email ||
                              (row.phone ? formatDisplayPhone(row.phone) : '') ||
                              'No contact info'}
                          </div>
                        </div>
                      </div>
                    </TableCell>

                    <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                      <div className="truncate">{row.email || '—'}</div>
                      {row.phone && (
                        <div className="text-xs text-muted-foreground/80 truncate">
                          {formatDisplayPhone(row.phone)}
                        </div>
                      )}
                    </TableCell>

                    <TableCell className="text-right pr-6">
                      <Badge
                        variant="secondary"
                        className="px-3 py-1 text-sm font-bold bg-primary/10 text-primary hover:bg-primary/15 rounded-lg"
                      >
                        {row.totalPoints.toLocaleString()} pts
                      </Badge>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Member Points Log Modal */}
      <Dialog
        open={Boolean(selectedMember)}
        onOpenChange={(open) => !open && setSelectedMember(null)}
      >
        <DialogContent className="w-[calc(100%-1.5rem)] sm:w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden rounded-2xl p-4 sm:p-6">
          <DialogHeader>
            <div className="flex items-center gap-3 pb-2 border-b pr-6">
              <Avatar className="h-10 w-10 border border-border shrink-0">
                <AvatarImage
                  src={selectedMember?.image || undefined}
                  alt={selectedMember?.userName}
                />
                <AvatarFallback className="bg-primary/10 text-primary text-sm font-bold">
                  {selectedMember?.userName
                    .split(' ')
                    .map((n) => n[0])
                    .join('')
                    .substring(0, 2)
                    .toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <DialogTitle className="text-lg sm:text-xl font-bold text-foreground truncate">
                  {selectedMember?.userName} - Points Log
                </DialogTitle>
                <DialogDescription className="text-xs text-muted-foreground">
                  Total Points:{' '}
                  <span className="font-semibold text-primary">
                    {selectedMember?.totalPoints.toLocaleString()} pts
                  </span>
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto mt-4 pr-1">
            {isLogsLoading ? (
              <div className="flex flex-col items-center justify-center py-12 space-y-3">
                <Loader2 className="h-7 w-7 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground font-medium animate-pulse">
                  Loading member points history...
                </p>
              </div>
            ) : !memberLogsData?.logs || memberLogsData.logs.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <History className="h-10 w-10 mx-auto mb-2 opacity-40 text-muted-foreground" />
                <p className="font-medium">No points activity logs found for this member.</p>
              </div>
            ) : (
              <div className="border rounded-xl overflow-hidden shadow-xs">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/40 hover:bg-muted/40">
                      <TableHead className="w-16 font-semibold">Sr No</TableHead>
                      <TableHead className="w-28 font-semibold">Points</TableHead>
                      <TableHead className="font-semibold">Notes</TableHead>
                      <TableHead className="text-right w-44 font-semibold pr-4">Time</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {memberLogsData.logs.map((log) => (
                      <TableRow key={log.id} className="hover:bg-muted/30 transition-colors">
                        <TableCell className="font-medium text-muted-foreground">
                          #{log.srNo}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="secondary"
                            className={
                              log.points >= 0
                                ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 font-bold border-emerald-500/30'
                                : 'bg-rose-500/15 text-rose-600 dark:text-rose-400 font-bold border-rose-500/30'
                            }
                          >
                            {log.points >= 0 ? `+${log.points}` : log.points}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm font-medium text-foreground">
                          {log.notes}
                        </TableCell>
                        <TableCell className="text-right text-xs text-muted-foreground pr-4 font-mono">
                          {formatLogTime(log.time)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Configure Hidden Members Dialog with Checkboxes */}
      <Dialog open={isConfigureHiddenOpen} onOpenChange={setIsConfigureHiddenOpen}>
        <DialogContent className="w-[calc(100%-1.5rem)] sm:w-full max-w-md max-h-[85vh] flex flex-col overflow-hidden rounded-2xl p-4 sm:p-6">
          <DialogHeader className="shrink-0">
            <DialogTitle className="text-lg sm:text-xl font-bold text-foreground flex items-center gap-2 pr-6">
              <EyeOff className="h-5 w-5 text-primary shrink-0" />
              <span>Configure Hidden Members</span>
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Check members to hide them from the points dashboard. Checked members will not be
              shown on the leaderboard.
            </DialogDescription>
          </DialogHeader>

          <div className="relative mt-3 shrink-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              type="search"
              placeholder="Search member username or email..."
              className="pl-9 bg-card rounded-xl text-sm w-full"
              value={hiddenSearchQuery}
              onChange={(e) => setHiddenSearchQuery(e.target.value)}
            />
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto mt-3 space-y-2 pr-1">
            {isMembersLoading ? (
              <div className="flex flex-col items-center justify-center py-10 space-y-2">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
                <p className="text-xs text-muted-foreground animate-pulse">
                  Loading members list...
                </p>
              </div>
            ) : filteredModalMembers.length === 0 ? (
              <div className="text-center py-8 text-sm text-muted-foreground">
                No members found matching your search.
              </div>
            ) : (
              filteredModalMembers.map((m) => {
                const memberUserId = m.id;
                const memberUserName = m.name || 'Unknown User';
                const isChecked = checkedHiddenUserIds.has(memberUserId);

                return (
                  <label
                    key={memberUserId}
                    className={`flex items-center justify-between gap-2 p-2.5 sm:p-3 rounded-xl border transition-colors cursor-pointer select-none ${
                      isChecked
                        ? 'bg-amber-500/10 border-amber-500/30'
                        : 'bg-card/60 hover:bg-accent/40 border-border/60'
                    }`}
                  >
                    <div className="flex items-center gap-2.5 min-w-0 flex-1 pr-1">
                      <Checkbox
                        id={`hide-${memberUserId}`}
                        checked={isChecked}
                        onCheckedChange={(checked) =>
                          handleCheckboxToggle(memberUserId, Boolean(checked))
                        }
                        className="rounded-md shrink-0"
                      />
                      <Avatar className="h-8 w-8 border shrink-0">
                        <AvatarImage src={m.image || undefined} alt={memberUserName} />
                        <AvatarFallback className="text-xs bg-primary/10 text-primary font-bold">
                          {memberUserName.substring(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-foreground leading-tight truncate">
                          {memberUserName}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5 truncate">
                          {m.email || (m.phone ? formatDisplayPhone(m.phone) : '') || 'Member'}
                        </div>
                      </div>
                    </div>

                    <Badge
                      variant="secondary"
                      className={
                        isChecked
                          ? 'bg-amber-500/20 text-amber-700 dark:text-amber-400 text-[10px] font-bold shrink-0'
                          : 'bg-muted text-muted-foreground text-[10px] font-medium shrink-0'
                      }
                    >
                      {isChecked ? 'Hidden' : 'Visible'}
                    </Badge>
                  </label>
                );
              })
            )}
          </div>

          <DialogFooter className="mt-3 shrink-0 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 border-t pt-3 bg-background z-10">
            <div className="text-xs text-muted-foreground font-medium text-center sm:text-left">
              <span className="font-bold text-foreground">{checkedHiddenUserIds.size}</span> member
              {checkedHiddenUserIds.size === 1 ? '' : 's'} checked (hidden)
            </div>
            <div className="flex items-center gap-2 justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsConfigureHiddenOpen(false)}
                className="rounded-xl flex-1 sm:flex-none"
              >
                Cancel
              </Button>

              <Button
                size="sm"
                onClick={handleSaveHiddenMembers}
                disabled={isSavingHidden}
                className="rounded-xl gap-1.5 flex-1 sm:flex-none"
              >
                {isSavingHidden ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Check className="h-3.5 w-3.5" />
                )}
                <span>Save Preferences</span>
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
